import { Box, Button, CircularProgress, Grid, Typography, useTheme } from '@mui/material'
import Bugsnag from '@bugsnag/js'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useNavigate, useParams } from 'react-router-dom'
import { MdDescription, MdPreview } from 'react-icons/md'
// Components
import { Formtitle, IperiaLayout } from '../../components'
// Hooks
import { useIperiaStatus } from '../../hooks/useIperiaStatus'
// Types
import type { IperiaDocument } from '../../types/iperiaStatus'
// Utils
import { documentsReviewPollInterval, hasDocumentAwaitingVerdict, rejectedRequiredDocuments } from '../../utils/iperiaDocumentsReview'
import { MAX_UPLOAD_BYTES, downscaleImageForUpload } from '../../utils/imageDownscale'

const ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
}


type DocumentDropZoneProps = {
  document: IperiaDocument
  previewUrl: string | null
  fileName: string | null
  uploadedRemotely?: boolean
  uploading?: boolean
  /** Pourquoi le dernier fichier choisi n'a pas été envoyé, affiché sous la pièce concernée. */
  error?: string | null
  onFileSelected: (file: File) => void
  onFileRejected: (message: string) => void
}

const DocumentDropZone = ({
  document,
  previewUrl,
  fileName,
  uploadedRemotely = false,
  uploading = false,
  error = null,
  onFileSelected,
  onFileRejected,
}: DocumentDropZoneProps) => {
  const theme = useTheme()
  const { isFocused, isDragAccept, isDragReject, getRootProps, getInputProps, open } = useDropzone({
    maxFiles: 1,
    multiple: false,
    accept: ACCEPT,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0]) {
        onFileSelected(acceptedFiles[0])
      }
    },
    // Sans message, un fichier refusé ne fait rien du tout : la personne retape sans comprendre.
    onDropRejected: (rejections) => {
      const code = rejections[0]?.errors[0]?.code
      onFileRejected(
        code === 'too-many-files'
          ? 'Choisissez un seul fichier.'
          : 'Format non accepté : envoyez un PDF, une photo JPG ou PNG.',
      )
    },
    noClick: Boolean(previewUrl) || uploadedRemotely,
    noKeyboard: Boolean(previewUrl) || uploadedRemotely,
  })

  const style = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderRadius: '4px!important',
      borderColor: isDragReject
        ? theme.palette.error.main
        : isDragAccept
          ? theme.palette.success.light
          : isFocused
            ? theme.palette.primary.main
            : theme.palette.grey[500],
      borderStyle: 'dashed',
      backgroundColor: theme.palette.common.white,
      color: theme.palette.text.secondary,
      transition: 'border .24s ease-in-out',
      cursor: previewUrl ? 'default' : 'pointer',
      minHeight: 120,
    }),
    [isDragAccept, isDragReject, isFocused, previewUrl, theme],
  )

  const isValidated = document.is_validated ?? false
  const isRejected = document.is_rejected ?? false

  const getDocumentStatus = (): string => {
    if (isValidated) return 'Enregistré'
    if (isRejected) return 'À mettre à jour'
    return 'En attente'
  }

  return (
    <Grid item xs={12} sx={style} {...getRootProps()}>
      <input {...getInputProps()} disabled={uploading} />
      {uploading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(2),
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2">Téléversement en cours...</Typography>
        </Box>
      ) : (previewUrl && fileName) || uploadedRemotely ? (
        <Box
          sx={{
            background: theme.palette.background.paper,
            padding: theme.spacing(1.5) + ' ' + theme.spacing(2.5),
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            minWidth: '300px',
            maxWidth: '800px',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: theme.spacing(2) }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                <MdDescription size={28} color={theme.palette.primary.dark} />
                <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, color: theme.palette.primary.dark }}>
                  {document.label}
                  {document.required ? ' *' : ''}
                </Typography>
              </Box>
              <Box
                sx={{
                  borderRadius: '5px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  backgroundColor: isValidated ? theme.palette.primary.main : isRejected ? theme.palette.error.main : theme.palette.primary.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isValidated || isRejected ? theme.palette.common.white : theme.palette.primary.main,
                  px: 1,
                  py: 0.5,
                  fontSize: '12px',
                  fontWeight: 600,
                  width: 'fit-content',
                }}
              >
                {getDocumentStatus()}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: theme.spacing(1), justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: theme.spacing(2), lg: 0 } }}>
              {previewUrl && (
                <Button
                  size="medium"
                  startIcon={<MdPreview />}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    window.open(previewUrl, '_blank')
                  }}
                  variant="outlined"
                  sx={{ textTransform: 'none', borderRadius: '100px' }}
                >
                  Prévisualiser
                </Button>
              )}
              <Button
                size="medium"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  open()
                }}
                variant="contained"
                sx={{ textTransform: 'none', borderRadius: '100px', color: theme.palette.common.white, backgroundColor: theme.palette.primary.dark, boxShadow: theme.shadows[1] }}
              >
                Remplacer
              </Button>
            </Box>
          </Box>
          {isRejected && document.rejection_reason && (
            <Box sx={{ display: 'flex', gap: theme.spacing(1), justifyContent: 'flex-start', border: `1px solid ${theme.palette.error.main}`, p: 1, borderRadius: '5px' }}>
              <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>Raison du rejet : {document.rejection_reason}</Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Grid item container direction="column" alignItems="center" justifyContent="center">
          <Typography variant="body1" sx={{ textAlign: 'center', color: theme.palette.text.primary }}>
            {document.label}
            {document.required ? ' *' : ''}
          </Typography>
          <Typography variant="caption">Déposez votre fichier ou cliquez ici</Typography>
          <Typography variant="caption">PDF, JPG ou PNG · 10 Mo max</Typography>
        </Grid>
      )}
      {error && (
        <Typography role="alert" variant="body2" color="error" sx={{ mt: 1, width: '100%' }}>
          {error}
        </Typography>
      )}
    </Grid>
  )
}

type LocalPreview = {
  url: string
  fileName: string
}

const Documents = () => {
  const navigate = useNavigate()
  const { global_id: globalId } = useParams<{ global_id: string }>()
  const { status, loading, error, refetch, uploadDocument, submitDocuments } = useIperiaStatus()
  const [localPreviews, setLocalPreviews] = useState<Record<string, LocalPreview>>({})
  const localPreviewsRef = useRef(localPreviews)
  localPreviewsRef.current = localPreviews
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [uploadErrors, setUploadErrors] = useState<Record<string, string | null>>({})
  const [uploading, setUploading] = useState<Record<string, boolean>>({})

  useEffect(() => {
    return () => {
      Object.values(localPreviewsRef.current).forEach((preview) => {
        URL.revokeObjectURL(preview.url)
      })
    }
  }, [])

  // Keep refreshing while a verdict is pending — a single delayed refetch is not enough in prod.
  const pollInterval = documentsReviewPollInterval(status)
  useEffect(() => {
    if (pollInterval === null) return undefined
    const intervalId = setInterval(() => {
      refetch().catch((err: Error) => Bugsnag.notify(err))
    }, pollInterval)
    return () => clearInterval(intervalId)
  }, [pollInterval, refetch])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !status) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Impossible de charger votre dossier.
      </Typography>
    )
  }

  const handleFileSelected = async (documentType: string, selected: File) => {
    setUploadErrors((previous) => ({ ...previous, [documentType]: null }))
    const file = await downscaleImageForUpload(selected)
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadErrors((previous) => ({
        ...previous,
        [documentType]: 'Fichier trop lourd (10 Mo max). Envoyez une photo ou un PDF plus léger.',
      }))
      return
    }
    setLocalPreviews((previous) => {
      const existing = previous[documentType]
      if (existing) {
        URL.revokeObjectURL(existing.url)
      }
      return {
        ...previous,
        [documentType]: {
          url: URL.createObjectURL(file),
          fileName: file.name,
        },
      }
    })

    setUploading((prev) => ({ ...prev, [documentType]: true }))
    try {
      await uploadDocument(file, documentType)
    } catch (err) {
      setUploadErrors((previous) => ({
        ...previous,
        [documentType]: "L'envoi n'a pas abouti. Vérifiez votre connexion puis choisissez à nouveau le fichier.",
      }))
      handleClear(documentType)
    } finally {
      setUploading((prev) => ({ ...prev, [documentType]: false }))
    }
  }

  const handleClear = (documentType: string) => {
    setLocalPreviews((previous) => {
      const existing = previous[documentType]
      if (existing) {
        URL.revokeObjectURL(existing.url)
      }
      const next = { ...previous }
      delete next[documentType]
      return next
    })
  }

  const isDocumentSatisfied = (documentType: string): boolean => {
    if (localPreviews[documentType]) return true
    return status.documents.some((doc) => doc.type === documentType && doc.uploaded)
  }

  const requiredDocuments = status.documents.filter((document) => document.required)
  const allRequiredDocumentsUploaded = requiredDocuments.every((document) => isDocumentSatisfied(document.type))
  const allRequiredDocumentsValidated = requiredDocuments.every((document) => document.is_validated && !document.is_rejected)
  const rejectedDocuments = rejectedRequiredDocuments(status.documents)
  const awaitingVerdict = hasDocumentAwaitingVerdict(status.documents)

  const handleSubmit = async () => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      await submitDocuments()
      navigate(`/iperia/${globalId}/parent-employeur`)
    } catch {
      setSubmitError('Une erreur est survenue. Veuillez réessayer.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    navigate(`/iperia/${globalId}/nir`)
  }

  return (
    <IperiaLayout
      productName={status.learner_summary.product_name}
      handleBack={handleBack}
      handleNext={handleSubmit}
      submitting={submitting}
      submittingNext={submitting || !allRequiredDocumentsUploaded || !allRequiredDocumentsValidated}
      alreadyConfirmed={allRequiredDocumentsUploaded}
    >
      <Formtitle
        title="Documents requis"
        subtitle="Veuillez déposer les documents demandés ci-dessous."
      />

      {rejectedDocuments.length > 0 && (
        <Box sx={{ border: '1px solid', borderColor: 'error.main', borderRadius: '5px', p: 1.5, mb: 2 }}>
          <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
            {rejectedDocuments.length === 1
              ? 'Un document doit être remplacé avant de continuer.'
              : `${rejectedDocuments.length} documents doivent être remplacés avant de continuer.`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Utilisez « Remplacer » sur chaque document signalé en rouge ci-dessous, puis cliquez sur Suivant.
          </Typography>
        </Box>
      )}

      {awaitingVerdict && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Vérification de vos documents en cours… Cette étape peut prendre quelques minutes.
          </Typography>
        </Box>
      )}

      <Grid container direction="column" spacing={2}>
        {status.documents.map((document) => {
          const preview = localPreviews[document.type]
          const uploadedDocument = status.documents.find((doc) => doc.type === document.type)
          const uploadedRemotely = !preview && uploadedDocument?.uploaded
          const previewUrl = uploadedRemotely ? uploadedDocument?.file_url : preview?.url
          const isUploading = uploading[document.type] || false
          const finalDocument = uploadedDocument || document

          return (
            <Grid item key={document.type} container direction="column">
              <DocumentDropZone
                document={finalDocument}
                previewUrl={previewUrl ?? null}
                fileName={preview?.fileName ?? null}
                uploadedRemotely={uploadedRemotely}
                uploading={isUploading}
                error={uploadErrors[document.type] ?? null}
                onFileSelected={(file) => handleFileSelected(document.type, file)}
                onFileRejected={(message) => setUploadErrors((previous) => ({ ...previous, [document.type]: message }))}
              />
            </Grid>
          )
        })}
      </Grid>
      {submitError && (
        <Typography color="error" sx={{ mt: 2 }}>
          {submitError}
        </Typography>
      )}
    </IperiaLayout>
  )
}

export default Documents
